///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { inject, Injectable } from '@angular/core';
import { BugReportDTO, EngineProcessTypeEnum, UserBugReportDTO } from '../../generated/shared-types.model';
import { from, Observable, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { GtthLocalStorageService } from '../auth/localStorage.service';
import { GtthHistoryService } from '../../project-view-panel/logical-topology/engine/history-service';
import { GtthProjectStateService } from '../../project-view-panel/logical-topology/engine/project-state.service';
import { GtthUpdateMainConfigCommand } from '../../project-view-panel/logical-topology/engine/commands/project/update-main-config-command';

@Injectable({
    providedIn: 'root'
})
export class GtthFeedbackService {
    private readonly httpClient = inject(HttpClient);
    private readonly localStorage = inject(GtthLocalStorageService);
    private readonly historyService = inject(GtthHistoryService);
    private readonly projectStateService = inject(GtthProjectStateService);

    private readonly generalReportUrl = 'api/notification';
    private readonly projectReportUrl = 'api/project';

    public getBugReportRequest(reportProblem: BugReportDTO): Observable<UserBugReportDTO> {
        const isProjectBugReport =
            this.projectStateService.projectIsOpened() &&
            this.projectStateService.getProject().engineType !== EngineProcessTypeEnum.BOM_CONFIGURATOR;

        const reportRequest$ = this.getRequest(reportProblem, isProjectBugReport);

        if (!isProjectBugReport || !this.historyService.isEditModeEnabled) {
            return reportRequest$;
        }

        return from(this.historyService.doCommandBypassHistory(this.historyService.command(GtthUpdateMainConfigCommand))).pipe(
            switchMap(() => reportRequest$)
        );
    }

    private getRequest(reportObject: BugReportDTO, isProjectBugReport: boolean): Observable<UserBugReportDTO> {
        const location = window.location;
        const protocol = location.protocol;
        const hostname = location.hostname;
        const port = location.port ? ':' + location.port : '';

        reportObject.locationOrigin = `${protocol}//${hostname}${port}`;
        reportObject.locationHash = `#${this.localStorage.getCurrentLocationUrl()}`;

        const url = isProjectBugReport
            ? `${this.projectReportUrl}/${this.projectStateService.getProjectId()}/bug-report`
            : `${this.generalReportUrl}/bug-report`;
        return this.httpClient.post<UserBugReportDTO>(url, reportObject);
    }
}
